import {
  RefObject,
  ReactElement,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Radio,
  RadioGroup,
  Skeleton,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useSuspenseQueries } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { useAtom, useAtomValue } from "jotai";
import {
  accessTokenInfoAtom,
  footerIndexAtom,
  outputStenScoreAtom,
} from "../store";
import {
  NotificationSettingQueries,
  notificationSettingQueries,
  saveNotificationSetting,
} from "../stores/notificationSettingStore";
import { BottomNavLayout } from "../components/templates/BottomNavLayout";
import { BasicPageLayout } from "../components/templates/BasicPageLayout";
import { LoadErrorBand } from "../components/molecules/LoadErrorBand";
import { SimpleAlertDialog } from "../components/atoms/SimpleAlertDialog";
import {
  INFO_ROW_CLASS,
  NotificationSettingSection,
} from "../components/molecules/NotificationSetting/NotificationSettingSection";
import { NotificationSettingInfo } from "../components/molecules/NotificationSetting/NotificationSettingInfo";
import {
  AlertKey,
  alertSections,
  reminderDays,
} from "../components/molecules/NotificationSetting/notificationSettingItems";
import {
  changeAlert,
  changeReminder,
  changeReport,
  followCompany,
  reminderChoice,
  toDraft,
} from "../components/molecules/NotificationSetting/notificationSettingDraft";
import { useModifiedTranslation } from "../hooks/useModifiedTranslation";
import { useToastMessage } from "../hooks/useToastMessage";

/*
 * 通知の受信設定画面。本人に届く通知メールの種別を選ぶ
 */
export function NotificationSetting(): ReactElement {
  const t = useModifiedTranslation();
  const [, setFooterIndex] = useAtom(footerIndexAtom);
  const tokenInfo = useAtomValue(accessTokenInfoAtom);
  // 開くたびに読み直す。「もう一度」も開き直したときと同じく4つとも読み直す
  const [openedAt, setOpenedAt] = useState(Date.now);
  const navigate = useNavigate();
  // 保存していない変更があるか。区分とボタンの部品が知らせる。読み込み中や読み込めなかったときは false のまま
  const hasUnsavedChangesRef = useRef(false);
  const [isConfirmingBack, setIsConfirmingBack] = useState(false);

  // 読み上げ文設定と同じく、ヘッダーの「戻る」だけ確認する。ボトムナビやブラウザの戻るでは確認しない
  const onBack = (): void => {
    if (hasUnsavedChangesRef.current) {
      setIsConfirmingBack(true);
      return;
    }
    navigate(-1);
  };

  // 設定画面の下の階層なので、ボトムナビは設定を選んだ状態にする
  useEffect(() => {
    setFooterIndex(3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BottomNavLayout>
      <BasicPageLayout
        title={t("Setting.itemTitleNotification")}
        titleProps={{ fontSize: "2xl" }}
        showBackButton
        onBack={onBack}
      >
        <Box color="common_text.dark">
          <Text
            fontWeight="normal"
            data-testid="notification-setting-description"
          >
            {t("NotificationSetting.description")}
          </Text>
          <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
              // 1つでも読めなければ帯を出す。メール配信設定だけ読めないときも、オフの人に届くと思わせないよう同じにする。
              // 帯は文字色を外の指定から受け継がず、テーマの既定の色（ダークでは明るい色）にする
              <Box color="chakra-body-text">
                <LoadErrorBand
                  error={error}
                  message={t("NotificationSetting.loadError")}
                  onRetry={() => resetErrorBoundary()}
                  testId="notification-setting-error"
                  retryTestId="notification-setting-retry"
                />
              </Box>
            )}
            onReset={() => setOpenedAt(Date.now())}
          >
            <Suspense fallback={<NotificationSettingSkeleton />}>
              <NotificationSettingForm
                queries={notificationSettingQueries(tokenInfo, openedAt)}
                hasUnsavedChangesRef={hasUnsavedChangesRef}
              />
            </Suspense>
          </ErrorBoundary>
        </Box>
        <SimpleAlertDialog
          isOpen={isConfirmingBack}
          okTestId="notification-setting-back-confirm"
          ok={t("Modal.yes")}
          cancel={t("Modal.no")}
          description={t("NotificationSetting.confirmBack")}
          onOk={() => {
            setIsConfirmingBack(false);
            navigate(-1);
          }}
          onClose={() => setIsConfirmingBack(false)}
        />
      </BasicPageLayout>
    </BottomNavLayout>
  );
}

// 読み込み中は、区分の形（見出しの細い棒と角丸のカード）の灰色の箱を3つ出す
function NotificationSettingSkeleton(): ReactElement {
  return (
    <Box data-testid="notification-setting-loading">
      {[0, 1, 2].map((i) => (
        <Box key={i} mt={6}>
          <Skeleton h={4} w={16} rounded="full" mb={3} />
          <Skeleton h={40} rounded="2xl" />
        </Box>
      ))}
    </Box>
  );
}

function NotificationSettingForm(
  props: Readonly<{
    queries: NotificationSettingQueries;
    hasUnsavedChangesRef: RefObject<boolean>;
  }>,
): ReactElement {
  const { hasUnsavedChangesRef } = props;
  const t = useModifiedTranslation();
  const outputStenScore = useAtomValue(outputStenScoreAtom);
  // 1つずつ読むと前の読み込みが終わるまで次を始めないので、4つまとめて同時に読む
  const [alert, reminder, report, emailDelivery] = useSuspenseQueries({
    queries: props.queries,
  });
  // 編集中の値は開いたときに1回だけ作る。開いている間は読み直さない
  const [draft, setDraft] = useState(() =>
    toDraft(alert.data, reminder.data, report.data),
  );
  const onAlertChange = (key: AlertKey, checked: boolean): void =>
    setDraft((d) => changeAlert(d, key, checked));
  const toast = useToast();
  const toastMessage = useToastMessage();
  const [isSaving, setIsSaving] = useState(false);
  // 開いたとき、または最後に保存できたときの値。今の値と違えば、保存していない変更がある
  const [savedDraft, setSavedDraft] = useState(draft);

  // 保存していない変更があるかを、画面の枠の「戻る」に知らせる。
  // 変えて元に戻しても、企業の値に従わなくなっているので変更ありになる
  useEffect(() => {
    hasUnsavedChangesRef.current =
      JSON.stringify(draft) !== JSON.stringify(savedDraft);
    return () => {
      hasUnsavedChangesRef.current = false;
    };
  }, [draft, savedDraft, hasUnsavedChangesRef]);

  // 保存しても読み直さない。送った値・開いたときの企業の値が、そのままサーバーの内容と同じため。
  // 失敗したら読み上げ文設定と同じくトーストで知らせ、入力は残す。もう一度押すと3つとも送り直す
  const save = async (): Promise<void> => {
    setIsSaving(true);
    try {
      await saveNotificationSetting(draft);
      setSavedDraft(draft);
      toast({
        title: t("NotificationSetting.saved"),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (e) {
      toastMessage(e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* 会社がメール配信をオフにしていると、ここで何を選んでも届かない */}
      {!emailDelivery.data.setting_data.email_enabled && (
        <Text
          mt={2}
          fontWeight="normal"
          color="orange.600"
          _dark={{ color: "orange.300" }}
          data-testid="notification-setting-email-disabled"
        >
          {t("NotificationSetting.emailDisabled")}
        </Text>
      )}
      {alertSections(outputStenScore).map(
        ({ section, info, keys, hasItemInfo }) => (
          <NotificationSettingSection
            key={section}
            title={t(`NotificationSetting.section.${section}`)}
            description={t(`NotificationSetting.info.${info}`)}
            followsCompany={draft.alert.followsCompany}
            testId={`notification-setting-${section}`}
          >
            {keys.map((key) => {
              const label = t(`NotificationSetting.alert.${key}`);
              const checkbox = (
                <Checkbox
                  key={key}
                  isChecked={draft.alert.value.mi1[key]}
                  onChange={(e) => onAlertChange(key, e.target.checked)}
                  data-testid={`notification-setting-${key}`}
                >
                  {label}
                </Checkbox>
              );
              if (!hasItemInfo) return checkbox;
              return (
                <Flex key={key} className={INFO_ROW_CLASS} align="center">
                  {checkbox}
                  <NotificationSettingInfo
                    title={label}
                    description={t(`NotificationSetting.alertInfo.${key}`)}
                    testId={`notification-setting-${key}`}
                  />
                </Flex>
              );
            })}
          </NotificationSettingSection>
        ),
      )}
      <NotificationSettingSection
        title={t("NotificationSetting.section.reminder")}
        description={t("NotificationSetting.info.reminder")}
        followsCompany={draft.reminder.followsCompany}
        testId="notification-setting-reminder"
      >
        <RadioGroup
          value={reminderChoice(draft.reminder.value)}
          onChange={(choice) => setDraft((d) => changeReminder(d, choice))}
        >
          <Radio value="none" data-testid="notification-setting-reminder-none">
            {t("NotificationSetting.reminder.none")}
          </Radio>
          {reminderDays.map((days) => (
            <Radio
              key={days}
              value={String(days)}
              data-testid={`notification-setting-reminder-${days}`}
            >
              {t(`NotificationSetting.reminder.days_${days}`)}
            </Radio>
          ))}
        </RadioGroup>
      </NotificationSettingSection>
      <NotificationSettingSection
        title={t("NotificationSetting.section.report")}
        description={t("NotificationSetting.info.report")}
        followsCompany={draft.report.followsCompany}
        testId="notification-setting-report"
      >
        <Checkbox
          isChecked={draft.report.value.enable_send_report}
          onChange={(e) => setDraft((d) => changeReport(d, e.target.checked))}
          data-testid="notification-setting-enable_send_report"
        >
          {t("NotificationSetting.report.enable_send_report")}
        </Checkbox>
      </NotificationSettingSection>
      <Button
        variant="btn_primary"
        w="full"
        size="lg"
        mt={8}
        isLoading={isSaving}
        onClick={save}
        data-testid="notification-setting-save"
      >
        {t("NotificationSetting.save")}
      </Button>
      <Button
        variant="ghost"
        color="primary.theme_lv1"
        w="full"
        mt={2}
        isDisabled={isSaving}
        onClick={() => setDraft(followCompany)}
        data-testid="notification-setting-use-company"
      >
        {t("NotificationSetting.useCompanySetting")}
      </Button>
    </>
  );
}
